// **React Imports
import { cloneElement, isValidElement, memo, useCallback, useMemo } from 'react'

// **MUI Imports
import { Tooltip } from '@mui/material'

// **Hooks Imports
import useDemoUser from '../../hooks/useDemoUser'

const DemoGuard = ({
  children,
  tooltipMessage = 'Action unavailable in Demo',
  placement = 'top',
  enableGuard = true,
  showTooltip = true
}) => {
  // hooks
  const { isDemoUser, isDemoTenant } = useDemoUser()

  // variables
  const shouldGuard = useMemo(
    () => isDemoUser && enableGuard,
    [isDemoUser, enableGuard]
  )

  const finalTooltip = useMemo(() => {
    return isDemoTenant
      ? tooltipMessage
      : 'You have readonly access. Mutating actions are disabled.'
  }, [isDemoTenant, tooltipMessage])

  // handlers
  const blockedClickHandler = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  // variables (derived)
  const guardedChild = useMemo(() => {
    if (!shouldGuard || !children) return children

    const isSingleElement = isValidElement(children) && !Array.isArray(children)
    const child = isSingleElement ? children : <span>{children}</span>

    return cloneElement(child, {
      ...child.props,
      disabled: true,
      onClick: blockedClickHandler,
      ...(typeof child.type === 'string' && child.type !== 'span'
        ? { type: 'button' }
        : {}),
      style: {
        ...(child.props?.style || {}),
        opacity: 0.6,
        cursor: 'not-allowed',
        pointerEvents: 'none'
      }
    })
  }, [shouldGuard, children, blockedClickHandler])

  if (!shouldGuard) {
    return children
  }

  if (!showTooltip) {
    return guardedChild
  }

  return (
    <Tooltip title={finalTooltip} arrow placement={placement}>
      <span style={{ cursor: 'not-allowed' }}>{guardedChild}</span>
    </Tooltip>
  )
}

export default memo(DemoGuard)
